// src/components/ClientUpdates.js
/**
 * @fileoverview Client Updates Page Component
 * Displays construction progress updates including:
 * - Current week summary with completed tasks and photos
 * - Previous weeks archive (expandable)
 * - Photo gallery with week/phase toggle
 *
 * @requires react
 * @requires ../hooks
 * @requires ../hooks/useUpdates
 * @requires ../utils/dateUtils
 * @requires react-icons/fi
 */
import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useClientProject } from "../../hooks";
import { useProjectUpdates } from "../../hooks/useUpdates";
import { formatDateET } from "../../utils/dateUtils";
import "./ClientUpdates.css";
import {
  FiCalendar,
  FiCheckCircle,
  FiCamera,
  FiFileText,
  FiPenTool,
  FiChevronDown,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiLayers,
  FiList,
  FiEye,
} from "react-icons/fi";
import { FaExclamationTriangle, FaTimes } from "react-icons/fa";

/**
 * ClientUpdates Component
 * Main component for displaying construction progress updates
 * @returns {JSX.Element} The rendered ClientUpdates page
 */
function ClientUpdates() {
  // State
  const [openWeekId, setOpenWeekId] = useState(null);
  const [visibleCount, setVisibleCount] = useState(5);
  const [photoGrouping, setPhotoGrouping] = useState("phase"); // 'week' or 'phase'
  const [photoView, setPhotoView] = useState("list"); // 'list' or 'gallery'
  const [openPhotoGroup, setOpenPhotoGroup] = useState(null);
  const [photoVisibleCount, setPhotoVisibleCount] = useState(5);
  const [selectedTask, setSelectedTask] = useState(null);
  const [galleryState, setGalleryState] = useState({
    isOpen: false,
    photos: [],
    currentIndex: 0,
  });

  // Get project data first
  const {
    data: projectResponse,
    isLoading: projectLoading,
    error: projectError,
    refetch: refetchProject,
  } = useClientProject();

  // Extract project - handle both nested and flat formats
  const project = projectResponse?.data || projectResponse;

  // Get updates data using project ID
  const {
    data: updatesResponse,
    isLoading: updatesLoading,
    error: updatesError,
    refetch: refetchUpdates,
  } = useProjectUpdates(project?.id);

  // Extract updates data - handle both nested and flat formats
  const updatesData = updatesResponse?.data || updatesResponse;

  // Use html ids
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#progress-photos") {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        const element = document.getElementById("progress-photos");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash]);

  // Combined loading state
  const isLoading = projectLoading || updatesLoading;
  const error = projectError || updatesError;

  /**
   * Toggle week expansion in archive
   * @param {string} weekId - The ID of the week to toggle
   */
  const toggleWeek = (id) => setOpenWeekId((prev) => (prev === id ? null : id));

  /**
   * Open task detail modal
   * @param {Object} task - The task object to display
   */
  const openTaskModal = (task) => {
    setSelectedTask(task);
  };

  /**
   * Close task modal
   */
  const closeTaskModal = () => {
    setSelectedTask(null);
  };

  /**
   * Open gallery at specific photo
   * @param {Array} photos - Array of photo objects
   * @param {number} startIndex - Index to start at
   */
  const openGallery = (photos, startIndex = 0) => {
    setGalleryState({
      isOpen: true,
      photos,
      currentIndex: startIndex,
    });
  };

  /**
   * Close gallery viewer
   */
  const closeGallery = () => {
    setGalleryState({
      isOpen: false,
      photos: [],
      currentIndex: 0,
    });
  };

  /**
   * Navigate gallery in given direction
   * @param {string} direction - 'next' or 'prev'
   */
  const navigateGallery = useCallback((direction) => {
    setGalleryState((prev) => {
      const newIndex =
        direction === "next"
          ? (prev.currentIndex + 1) % prev.photos.length
          : (prev.currentIndex - 1 + prev.photos.length) % prev.photos.length;
      return { ...prev, currentIndex: newIndex };
    });
  }, []);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryState.isOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          navigateGallery("prev");
          break;
        case "ArrowRight":
          navigateGallery("next");
          break;
        case "Escape":
          closeGallery();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [galleryState.isOpen, navigateGallery]);

  /**
   * Get photo groups based on current view mode
   * @returns {Array} Array of photo groups
   */
  const getPhotoGroups = () => {
    if (!updatesData) return [];
    return photoGrouping === "week"
      ? updatesData.photosByWeek || []
      : updatesData.photosByPhase || [];
  };

  const handleSetPhotoGrouping = (next) => {
    setPhotoGrouping(next);
    setOpenPhotoGroup(null);
    setPhotoVisibleCount(5);
  };

  const togglePhotoGroup = (id) =>
    setOpenPhotoGroup((prev) => (prev === id ? null : id));

  /**
   * Format group label for photo sections
   * @param {Object} group - The photo group object
   * @returns {string} Formatted label
   */
  const formatGroupLabel = (group) => {
    if (photoGrouping === "week" && group.weekOf) {
      return `Week of ${formatDateET(group.weekOf)}`;
    }
    return group.phase || group.groupLabel || "Unassigned";
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="component-loading">
          <div className="component-spinner"></div>
          <p>Loading project updates...</p>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="dashboard-error">
          <FaExclamationTriangle />
          <h2>Unable to load Updates</h2>
          <p>{(projectError || updatesError)?.message}</p>
          <button
            onClick={() => {
              refetchProject();
              refetchUpdates();
            }}
          >
            Try Again
          </button>
        </div>
      </>
    );
  }

  // No project found
  if (!project || !project.id) {
    return (
      <>
        <div className="state-container">
          <FiImage className="state-icon" />
          <h2 className="state-title">Welcome to My Red Setter Home</h2>
          <p className="state-message">
            No active project found for your account.
          </p>
          <p className="state-message">
            Please contact your project manager for assistance.
          </p>
        </div>
      </>
    );
  }

  const currentWeek = updatesData?.currentWeek || {};
  const previousWeeks = updatesData?.previousWeeks || [];
  const photoGroups = getPhotoGroups();
  const currentPhase =
    updatesData?.currentPhase || project?.constructionPhase || "";

  return (
    <>
      <div className="client-updates">
        <div className="page-container">
          {/* Page Header */}
          <div className="page-header">
            <h1 className="page-title">Project Updates</h1>
            <p className="page-subtitle">
              Track your home's construction progress
            </p>
          </div>

          {/* Current Week Summary Card */}
          <section className="updates-current-week card">
            <div className="updates-current-header">
              <div className="updates-current-icon">
                <FiCalendar />
              </div>
              <div className="updates-current-title">
                <h2>This Week's Progress</h2>
                {currentWeek.weekOf && (
                  <span className="updates-week-label">
                    Week of {formatDateET(currentWeek.weekOf)}
                  </span>
                )}
              </div>
            </div>
            {/* Stats Row */}
            <div className="updates-current-stats">
              <div className="updates-stat-item">
                <div className="updates-stat-icon tasks">
                  <FiCheckCircle />
                </div>
                <div className="updates-stat-content">
                  <span className="updates-stat-value">
                    {currentWeek.tasksCompleted || 0}
                  </span>
                  <span className="updates-stat-label">Tasks Completed</span>
                </div>
              </div>
              <div className="updates-stat-item">
                <div className="updates-stat-icon photos">
                  <FiCamera />
                </div>
                <div className="updates-stat-content">
                  <span className="updates-stat-value">
                    {currentWeek.photosAdded || 0}
                  </span>
                  <span className="updates-stat-label">Photos Added</span>
                </div>
              </div>
              <div className="updates-stat-item">
                <div className="updates-stat-icon phase">
                  <FiLayers />
                </div>
                <div className="updates-stat-content">
                  <span className="updates-stat-value">
                    {currentPhase || "—"}
                  </span>
                  <span className="updates-stat-label">Current Phase</span>
                </div>
              </div>
            </div>
            {/* Current Week Tasks */}
            {currentWeek.tasks && currentWeek.tasks.length > 0 && (
              <div className="updates-current-tasks">
                <h4 className="updates-section-title">Completed This Week</h4>
                <ul className="updates-task-list">
                  {currentWeek.tasks.map((task) => (
                    <li
                      key={task.id}
                      className="updates-task-item"
                      onClick={() => openTaskModal(task)}
                    >
                      <FiCheckCircle className="updates-task-check" />
                      <div className="updates-task-content">
                        <span className="updates-task-name">{task.name}</span>
                        <span className="updates-task-date-mobile">
                          {formatDateET(task.dateCompleted)}
                        </span>
                      </div>
                      <div className="updates-task-meta">
                        <span className="updates-task-date-desktop">
                          {formatDateET(task.dateCompleted)}
                        </span>
                        {task.attachments && task.attachments.length > 0 && (
                          <span className="updates-task-photos-badge">
                            <FiCamera /> {task.attachments.length}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(!currentWeek.tasks || currentWeek.tasks.length === 0) && (
              <p className="updates-section-title updates-section-title-empty">
                No tasks completed this week yet.
              </p>
            )}
            {/* Previous Weeks Archive */}
            {previousWeeks.length > 0 && (
              <section className="updates-archive">
                <div className="updates-divider"></div>
                <h3 className="updates-archive-title">Previous Weeks</h3>
                <div className="updates-archive-list">
                  {previousWeeks.slice(0, visibleCount).map((week) => (
                    <div key={week.id} className="updates-archive-week">
                      <div
                        className="updates-archive-header"
                        onClick={() => toggleWeek(week.id)}
                      >
                        <div className="updates-archive-info">
                          <FiCalendar className="updates-archive-icon" />
                          <span className="updates-archive-label">
                            Week of {formatDateET(week.weekOf)}
                          </span>
                          <span className="updates-archive-count hide-sm">
                            {week.tasksCompleted} task
                            {week.tasksCompleted !== 1 ? "s" : ""}
                          </span>
                          <span className="updates-archive-count show-sm">
                            <FiCheckCircle /> {week.tasksCompleted}
                          </span>
                        </div>
                        <FiChevronDown
                          className={`updates-archive-chevron ${
                            openWeekId === week.id ? "expanded" : ""
                          }`}
                        />
                      </div>

                      {openWeekId === week.id && (
                        <div className="updates-archive-content">
                          <ul className="updates-archive-task-list">
                            {week.tasks.map((task) => (
                              <li
                                key={task.id}
                                className="updates-archive-task"
                                role="button"
                                tabIndex={0}
                                onClick={() => openTaskModal(task)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openTaskModal(task);
                                  }
                                }}
                              >
                                <FiCheckCircle className="updates-task-check" />
                                <div className="updates-task-content">
                                  <span className="updates-task-name">
                                    {task.name}
                                  </span>
                                  <span className="updates-task-date-mobile">
                                    {formatDateET(task.dateCompleted)}
                                  </span>
                                </div>
                                <div className="updates-task-meta">
                                  <span className="updates-task-date-desktop">
                                    {formatDateET(task.dateCompleted)}
                                  </span>
                                  {task.attachments &&
                                    task.attachments.length > 0 && (
                                      <span className="updates-task-photos-badge">
                                        <FiCamera /> {task.attachments.length}
                                      </span>
                                    )}
                                  <FiChevronRight className="updates-archive-task-chevron" />
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {visibleCount < previousWeeks.length && (
                  <div className="updates-archive-more">
                    <button
                      type="button"
                      className="updates-archive-more-btn"
                      onClick={() => setVisibleCount((prev) => prev + 5)}
                    >
                      Load more weeks
                    </button>
                    <span className="updates-archive-more-count">
                      Showing {Math.min(visibleCount, previousWeeks.length)} of{" "}
                      {previousWeeks.length} weeks
                    </span>
                  </div>
                )}
              </section>
            )}{" "}
          </section>

          {/* Photo Gallery Section */}
          <section
            id="progress-photos"
            className="updates-photos-section card"
          >
            <div className="updates-photos-header">
              <div className="updates-photos-title-wrap">
                <div className="updates-photos-icon">
                  <FiCamera />
                </div>
                <div>
                  <h3 className="updates-photos-title">Progress Photos</h3>
                  <p className="updates-photos-subtitle">
                    Visual documentation of your home's construction
                  </p>
                </div>
              </div>
              <div className="updates-photos-toolbar">
                  <div className="updates-group-toggle">
                    <span className="show-sm">Filter</span>
                    <button
                      className={`updates-group-btn ${
                        photoGrouping === "week" ? "active" : ""
                      }`}
                      onClick={() => handleSetPhotoGrouping("week")}
                    >
                      <FiCalendar /> <span>By Week</span>
                    </button>
                    <button
                      className={`updates-group-btn ${
                        photoGrouping === "phase" ? "active" : ""
                      }`}
                      onClick={() => handleSetPhotoGrouping("phase")}
                    >
                      <FiLayers /> <span>By Phase</span>
                    </button>
                  </div>
                  <div className="updates-group-toggle">
                    <span className="show-sm">View</span>
                    <button
                      className={`updates-group-btn ${
                        photoView === "list" ? "active" : ""
                      }`}
                      onClick={() => setPhotoView("list")}
                    >
                      <FiList /> <span>List</span>
                    </button>
                    <button
                      className={`updates-group-btn ${
                        photoView === "gallery" ? "active" : ""
                      }`}
                      onClick={() => setPhotoView("gallery")}
                    >
                      <FiImage /> <span>Gallery</span>
                    </button>
                  </div>
                </div>
            </div>

            {photoGroups.length === 0 ? (
              <div className="updates-photos-empty">
                <FiCamera className="updates-photos-empty-icon" />
                <p className="updates-photos-empty-text">
                  Progress photos will appear here as your home's construction
                  is documented.
                </p>
              </div>
            ) : photoView === "gallery" ? (
                <div className="updates-photo-groups">
                  {(photoGrouping === "week"
                    ? photoGroups.slice(0, photoVisibleCount)
                    : photoGroups
                  ).map((group) => (
                    <div
                      key={group.id || group.groupId}
                      className="updates-photo-group"
                    >
                      <h4 className="updates-group-label">
                        {photoGrouping === "week" ? (
                          <FiCalendar />
                        ) : (
                          <FiLayers />
                        )}
                        {formatGroupLabel(group)}
                        <span className="updates-group-count">
                          {group.photos.length} photo
                          {group.photos.length !== 1 ? "s" : ""}
                        </span>
                      </h4>
                      <div className="updates-photo-grid">
                        {group.photos.map((photo, index) => (
                          <div
                            key={photo.id}
                            className="updates-photo-card"
                            onClick={() => openGallery(group.photos, index)}
                          >
                            <div className="updates-photo-thumbnail">
                              <img
                                src={photo.thumbnail || photo.url}
                                alt={
                                  photo.name ||
                                  photo.filename ||
                                  "Progress photo"
                                }
                                loading="lazy"
                              />
                              <div className="updates-photo-overlay">
                                <FiEye />
                                <span>{photo.name}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="updates-photo-list-container">
                  {(photoGrouping === "week"
                    ? photoGroups.slice(0, photoVisibleCount)
                    : photoGroups
                  ).map((group) => {
                    const groupId = group.id || group.groupId;
                    const isOpen = openPhotoGroup === groupId;
                    return (
                      <div key={groupId} className="updates-photo-list-row">
                        <div
                          className={`updates-photo-list-header ${
                            isOpen ? "open" : ""
                          }`}
                          onClick={() => togglePhotoGroup(groupId)}
                        >
                          {photoGrouping === "week" ? (
                            <FiCalendar className="updates-photo-list-icon" />
                          ) : (
                            <FiLayers className="updates-photo-list-icon" />
                          )}
                          <span className="updates-photo-list-label">
                            {formatGroupLabel(group)}
                          </span>
                          <span className="updates-photo-list-count">
                            <FiCamera /> {group.photos.length}
                          </span>
                          <FiChevronDown
                            className={`updates-photo-list-chevron ${
                              isOpen ? "expanded" : ""
                            }`}
                          />
                        </div>
                        <div
                          className={`updates-photo-list-body ${
                            isOpen ? "open" : ""
                          }`}
                        >
                          <div className="updates-photo-grid">
                            {group.photos.map((photo, index) => (
                              <div
                                key={photo.id}
                                className="updates-photo-card"
                                onClick={() => openGallery(group.photos, index)}
                              >
                                <div className="updates-photo-thumbnail">
                                  <img
                                    src={photo.thumbnail || photo.url}
                                    alt={
                                      photo.name ||
                                      photo.filename ||
                                      "Progress photo"
                                    }
                                    loading="lazy"
                                  />
                                  <div className="updates-photo-overlay">
                                    <FiEye />
                                    <span>{photo.name}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {photoGrouping === "week" &&
                photoVisibleCount < photoGroups.length && (
                  <div className="updates-archive-more">
                    <button
                      type="button"
                      className="updates-archive-more-btn"
                      onClick={() => setPhotoVisibleCount((prev) => prev + 5)}
                    >
                      Load more weeks
                    </button>
                    <span className="updates-archive-more-count">
                      Showing {Math.min(photoVisibleCount, photoGroups.length)}{" "}
                      of {photoGroups.length} weeks
                    </span>
                  </div>
                )}
          </section>
        </div>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="modal-overlay" onClick={closeTaskModal}>
          <div
            className="modal-container modal-sm digest-task-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={closeTaskModal}
              aria-label="Close modal"
            >
              <FaTimes />
            </button>

            <div className="digest-task-modal-header">
              <FiCheckCircle className="digest-task-modal-icon" />
              <h3>{selectedTask.name}</h3>
            </div>

            <div className="digest-task-modal-body">
              <div className="digest-task-modal-meta">
                {selectedTask.constructionPhase && (
                  <span className="digest-meta-badge phase">
                    <FiLayers /> {selectedTask.constructionPhase}
                  </span>
                )}
                <span className="digest-meta-badge date">
                  <FiCheckCircle /> Completed{" "}
                  {formatDateET(selectedTask.dateCompleted)}
                </span>
              </div>

              {selectedTask.description && (
                <div className="digest-task-modal-section">
                  <h4>
                    <FiFileText /> Task Description
                  </h4>
                  <p className="digest-task-modal-text">
                    {selectedTask.description}
                  </p>
                </div>
              )}

              {selectedTask.notes && (
                <div className="digest-task-modal-section">
                  <h4>
                    <FiPenTool /> Project Manager's Notes
                  </h4>
                  <p className="digest-task-modal-text">{selectedTask.notes}</p>
                </div>
              )}

              {selectedTask.attachments &&
                selectedTask.attachments.length > 0 && (
                  <div className="digest-task-modal-section">
                    <h4>
                      <FiCamera /> Photos ({selectedTask.attachments.length})
                    </h4>
                    <div className="digest-task-modal-photos">
                      {selectedTask.attachments.map((photo, index) => (
                        <div
                          key={photo.id || index}
                          className="digest-task-modal-photo"
                          onClick={() =>
                            openGallery(selectedTask.attachments, index)
                          }
                        >
                          <img
                            src={photo.thumbnail || photo.url}
                            alt={photo.filename || "Task photo"}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Viewer */}
      {galleryState.isOpen && galleryState.photos.length > 0 && (
        <div className="modal-overlay" onClick={closeGallery}>
          <div className="updates-gallery" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeGallery}>
              <FiX />
            </button>

            {galleryState.photos.length > 1 && (
              <>
                <button
                  className="updates-gallery-nav updates-gallery-prev"
                  onClick={() => navigateGallery("prev")}
                >
                  <FiChevronLeft />
                </button>
                <button
                  className="updates-gallery-nav updates-gallery-next"
                  onClick={() => navigateGallery("next")}
                >
                  <FiChevronRight />
                </button>
              </>
            )}

            <div className="updates-gallery-image-wrap">
              <img
                className="updates-gallery-image"
                src={galleryState.photos[galleryState.currentIndex]?.url}
                alt={
                  galleryState.photos[galleryState.currentIndex]?.name ||
                  galleryState.photos[galleryState.currentIndex]?.filename ||
                  "Photo"
                }
              />
            </div>

            <div className="updates-gallery-info">
              <div className="updates-gallery-details">
                <p className="updates-gallery-task-name">
                  <FiCheckCircle />
                  {galleryState.photos[galleryState.currentIndex]?.name ||
                    galleryState.photos[galleryState.currentIndex]?.filename}
                </p>
                {galleryState.photos[galleryState.currentIndex]?.uploadDate && (
                  <span className="updates-gallery-date">
                    <FiCalendar />
                    {formatDateET(
                      galleryState.photos[galleryState.currentIndex].uploadDate,
                    )}
                  </span>
                )}
              </div>
              <div className="updates-gallery-pagination">
                <span className="updates-gallery-counter">
                  {galleryState.currentIndex + 1} / {galleryState.photos.length}
                </span>
                {galleryState.photos.length > 1 && (
                  <div className="updates-gallery-dots">
                    {galleryState.photos.map((_, index) => (
                      <button
                        key={index}
                        className={`updates-gallery-dot ${
                          index === galleryState.currentIndex ? "active" : ""
                        }`}
                        onClick={() =>
                          setGalleryState((prev) => ({
                            ...prev,
                            currentIndex: index,
                          }))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientUpdates;
